import {
  applyMiddleware, compose,
  createStore, Store
} from 'redux';
import thunk from 'redux-thunk';
import u = require('updeep');
import { ApplicationAction } from './actions/types';
import * as m from './middleware';
import initialState, { State } from './state';
import { ExtraArguments } from './extraArguments';

const devToolsExtension = (window as any).__REDUX_DEVTOOLS_EXTENSION__;

// updeep deep-merges objects/arrays; dashboard payloads must replace these wholesale.
const REPLACE_FIELDS: Array<keyof State> = [
  'profilePledgeCompletions',
  'profileMemberships',
  'profileEntityRanks',
  'user',
  'userEmails',
  'competitionUser',
  'competitionUserView',
  'competition',
  'userMemberships',
  'entities',
  'users',
  'token',
  'competitionUserScores',
  'competitionScoreLevels',
  'competitionPledges',
  'competitionPledgeCompletionTotals',
  'competitionPledgeCategories',
  'userPledgeCompletions',
  'competitionPledgeIDsByUUID',
  'competitionEntities',
  'competitionEntityScores',
  'competitionTopUserScores',
  'calculatorSnapshot',
  // Story feed arrays must replace, not index-merge — otherwise new actions
  // corrupt/vanish when returning to the home feed after Take action.
  'stories',
  'pendingFeedStories',
  'storyPagination',
  'storyLikes',
  'storyFlags',
  'storyComments',
  'storyCommentPagination',
  'storyCommentsLikes',
  'storyCommentsFlags',
  'storyTaggedUserIDs',
  'latestToPledge'
];

const isUpdeepConstant = (value: any) => typeof value === 'function';

const reducer = (state: State = initialState, action: ApplicationAction): State => {
  if (action.payload === undefined) return state;

  const update: Partial<State> = { ...action.payload };
  REPLACE_FIELDS.forEach((field) => {
    if (action.payload![field] !== undefined) {
      const incoming = (action.payload as any)[field];
      // Already wrapped by the action (e.g. setStoryQuery) — keep as-is.
      if (isUpdeepConstant(incoming)) {
        (update as any)[field] = incoming;
        return;
      }
      // Partial page loads (pledge/entity/profile) often return incomplete maps.
      // Merging preserves current-user points and known users used by Profile/Dashboard.
      if (field === 'competitionUserScores' || field === 'users'
        || field === 'userPledgeCompletions' || field === 'userPledgePoints') {
        (update as any)[field] = u.constant({
          ...((state as any)[field] || {}),
          ...(incoming || {})
        });
      } else if (field === 'stories') {
        // Full feed arrays must replace. Like/flag toggles still patch by index object.
        if (Array.isArray(incoming)) {
          (update as any).stories = u.constant(incoming);
        }
      } else if (
        field === 'storyLikes' || field === 'storyFlags'
        || field === 'storyCommentsLikes' || field === 'storyCommentsFlags'
        || field === 'storyTaggedUserIDs' || field === 'storyComments'
        || field === 'storyCommentPagination' || field === 'latestToPledge'
      ) {
        // Reaction / comment maps are often partial patches — merge keys.
        (update as any)[field] = u.constant({
          ...((state as any)[field] || {}),
          ...(incoming || {})
        });
      } else {
        (update as any)[field] = u.constant(incoming);
      }
    }
  });

  return u(update, state);
};
export const createCompetitionStore = (extra: ExtraArguments): Store<State> => {
  const middleware = [
    m.storage,
    m.crashReporter,
    thunk.withExtraArgument(extra)
  ];

  return createStore(
    reducer,
    initialState,
    devToolsExtension ?
      compose(applyMiddleware(...middleware), devToolsExtension()) :
      compose(applyMiddleware(...middleware))
  );
};
